import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { useLeadership } from '../hooks/useLeadership';
import { companyOverrideService } from '../services/company-override.service';
import { LeadershipEntry, LeadershipCreatePayload } from '../types/override.types';
import { api } from '../../../../core/utils/apiClient';

interface AuditLogEntry {
  id: number;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  performed_by: string;
  extra_metadata: Record<string, any> | null;
  created_at: string;
}

const LEADERSHIP_ACTION_LABELS: Record<string, string> = {
  leadership_added: 'Added',
  leadership_imported: 'Imported',
  leadership_updated: 'Updated',
  leadership_deleted: 'Deleted',
  headshot_uploaded: 'Photo uploaded',
  headshot_deleted: 'Photo removed',
};

const LEADERSHIP_ACTION_COLOR: Record<string, string> = {
  leadership_added: '#059669',
  leadership_imported: '#0891B2',
  leadership_updated: '#D97706',
  leadership_deleted: '#DC2626',
  headshot_uploaded: '#6B7280',
  headshot_deleted: '#6B7280',
};

interface Props {
  companyId: number;
}

// ── Headshot with authenticated fetch ──

const AuthenticatedHeadshot: React.FC<{ companyId: number; leaderId: string; name: string }> = ({
  companyId,
  leaderId,
  name,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    companyOverrideService.fetchHeadshotBlob(companyId, leaderId).then((url) => {
      if (url) {
        revoke = url;
        setBlobUrl(url);
      }
    });
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [companyId, leaderId]);

  if (!blobUrl) {
    const initials = name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    return (
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold"
        style={{ backgroundColor: COLORS.admin.primary }}
      >
        {initials}
      </div>
    );
  }

  return <img src={blobUrl} alt={name} className="w-12 h-12 rounded-full object-cover" />;
};

// ── Main Component ──

export const EditLeadershipTab: React.FC<Props> = ({ companyId }) => {
  const {
    leaders,
    isLoading,
    isSubmitting,
    error,
    createLeader,
    updateLeader,
    deleteLeader,
    uploadHeadshot,
    deleteHeadshot,
    importFromEnrichment,
  } = useLeadership(companyId);

  const [showForm, setShowForm] = useState(false);
  const [editingLeader, setEditingLeader] = useState<LeadershipEntry | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  // Form state
  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [email, setEmail] = useState('');
  const [orderPosition, setOrderPosition] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [headshotLeaderId, setHeadshotLeaderId] = useState<string | null>(null);

  const resetForm = () => {
    setFullName('');
    setTitle('');
    setBio('');
    setLinkedinUrl('');
    setEmail('');
    setOrderPosition(0);
    setFormError(null);
  };

  const openAddForm = () => {
    resetForm();
    setEditingLeader(null);
    setShowForm(true);
  };

  const openEditForm = (leader: LeadershipEntry) => {
    setFullName(leader.full_name);
    setTitle(leader.title || '');
    setBio(leader.bio || '');
    setLinkedinUrl(leader.linkedin_url || '');
    setEmail(leader.email || '');
    setOrderPosition(leader.order_position);
    setFormError(null);
    setEditingLeader(leader);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingLeader(null);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setFormError('Full name is required');
      return;
    }
    setFormError(null);

    const payload: LeadershipCreatePayload = {
      full_name: fullName.trim(),
      title: title.trim() || undefined,
      bio: bio.trim() || undefined,
      linkedin_url: linkedinUrl.trim() || undefined,
      email: email.trim() || undefined,
      order_position: orderPosition,
    };

    try {
      if (editingLeader) {
        await updateLeader(editingLeader.id, payload);
      } else {
        await createLeader(payload);
      }
      closeForm();
    } catch (err: any) {
      setFormError(err.message || 'Operation failed');
    }
  };

  const handleDelete = async (leaderId: string) => {
    try {
      await deleteLeader(leaderId);
      setConfirmDeleteId(null);
    } catch (err: any) {
      setFormError(err.message || 'Delete failed');
    }
  };

  const handleHeadshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !headshotLeaderId) return;
    try {
      await uploadHeadshot(headshotLeaderId, file);
    } catch (err: any) {
      setFormError(err.message || 'Headshot upload failed');
    }
    setHeadshotLeaderId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleHeadshotDelete = async (leaderId: string) => {
    try {
      await deleteHeadshot(leaderId);
    } catch (err: any) {
      setFormError(err.message || 'Headshot delete failed');
    }
  };

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<AuditLogEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await api.get(`/api/v1/companies/${companyId}/audit-logs?limit=100`);
      if (!response.ok) {
        if (response.status === 403) {
          setHistoryError('You need admin or owner access to view change history.');
        } else {
          setHistoryError('Failed to load change history.');
        }
        return;
      }
      const data: AuditLogEntry[] = await response.json();
      setHistoryLogs(data.filter((entry) => entry.action.startsWith('leadership_') || entry.action.startsWith('headshot_')));
    } catch {
      setHistoryError('Failed to load change history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [companyId]);

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && historyLogs === null && !historyLoading) {
      loadHistory();
    }
  };

  const autoImportAttempted = useRef(false);

  const tryAutoImport = useCallback(async () => {
    if (autoImportAttempted.current) return;
    autoImportAttempted.current = true;
    try {
      const result = await importFromEnrichment();
      if (result.imported_count > 0) {
        setImportMessage(`Imported ${result.imported_count} leader${result.imported_count === 1 ? '' : 's'} from enrichment data.`);
      }
    } catch {
      // Silent — auto-import is best-effort. User can still add manually.
    }
  }, [importFromEnrichment]);

  useEffect(() => {
    if (!isLoading && leaders.length === 0 && !autoImportAttempted.current) {
      tryAutoImport();
    }
  }, [isLoading, leaders.length, tryAutoImport]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
        />
        <span className="ml-3 text-sm text-gray-500">Loading leadership data...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Edit Leadership</h3>
        <button
          onClick={openAddForm}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
          style={{ backgroundColor: COLORS.admin.primary }}
        >
          + Add Leader
        </button>
      </div>

      {error && <p className="text-sm mb-4" style={{ color: '#EF4444' }}>{error}</p>}
      {formError && <p className="text-sm mb-4" style={{ color: '#EF4444' }}>{formError}</p>}
      {importMessage && <p className="text-sm mb-4" style={{ color: '#059669' }}>{importMessage}</p>}

      {/* Hidden file input for headshot uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleHeadshotUpload}
      />

      {/* Leader List */}
      {leaders.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No leadership entries yet. Click "Add Leader" to create one.</p>
      ) : (
        <div className="space-y-3">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className="flex items-start gap-4 p-4 rounded-lg border transition-colors"
              style={{ borderColor: '#E5E7EB' }}
            >
              {/* Headshot */}
              {leader.headshot_url ? (
                <AuthenticatedHeadshot companyId={companyId} leaderId={leader.id} name={leader.full_name} />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  {leader.full_name
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{leader.full_name}</p>
                  {leader.is_manual_override && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">Manual</span>
                  )}
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{leader.source}</span>
                </div>
                {leader.title && <p className="text-sm text-gray-600">{leader.title}</p>}
                {leader.bio && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{leader.bio}</p>}
                {leader.linkedin_url && (
                  <a
                    href={leader.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs mt-1 inline-block"
                    style={{ color: COLORS.admin.primary }}
                  >
                    LinkedIn Profile
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    setHeadshotLeaderId(leader.id);
                    fileInputRef.current?.click();
                  }}
                  disabled={isSubmitting}
                  className="px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Upload headshot"
                >
                  Photo
                </button>
                {leader.headshot_url && (
                  <button
                    onClick={() => handleHeadshotDelete(leader.id)}
                    disabled={isSubmitting}
                    className="px-2 py-1 text-xs font-medium rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title="Remove headshot"
                  >
                    Del Photo
                  </button>
                )}
                <button
                  onClick={() => openEditForm(leader)}
                  disabled={isSubmitting}
                  className="px-3 py-1 text-xs font-medium rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  Edit
                </button>
                {confirmDeleteId === leader.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(leader.id)}
                      disabled={isSubmitting}
                      className="px-2 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(leader.id)}
                    disabled={isSubmitting}
                    className="px-3 py-1 text-xs font-medium rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Change history */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={toggleHistory}
          className="text-xs font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
            {historyOpen ? <path d="M2 6 L5 3 L8 6" /> : <path d="M3 2 L7 5 L3 8" />}
          </svg>
          Change history
          {historyLogs && <span className="text-gray-400 font-normal">({historyLogs.length})</span>}
          {historyOpen && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); loadHistory(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); loadHistory(); } }}
              className="ml-2 text-gray-400 hover:text-gray-600 cursor-pointer text-[10px] underline"
            >
              refresh
            </span>
          )}
        </button>
        {historyOpen && (
          <div className="mt-3">
            {historyLoading && <p className="text-xs text-gray-400">Loading history…</p>}
            {historyError && <p className="text-xs" style={{ color: '#DC2626' }}>{historyError}</p>}
            {!historyLoading && !historyError && historyLogs && historyLogs.length === 0 && (
              <p className="text-xs text-gray-400">No leadership changes yet.</p>
            )}
            {!historyLoading && historyLogs && historyLogs.length > 0 && (
              <div className="space-y-2">
                {historyLogs.map((entry) => {
                  const label = LEADERSHIP_ACTION_LABELS[entry.action] ?? entry.action;
                  const color = LEADERSHIP_ACTION_COLOR[entry.action] ?? '#6B7280';
                  const name = entry.new_value || entry.old_value || '—';
                  const meta = entry.extra_metadata || {};
                  const detailBits: string[] = [];
                  if (entry.action === 'leadership_updated' && entry.old_value && entry.new_value && entry.old_value !== entry.new_value) {
                    detailBits.push(`renamed from "${entry.old_value}"`);
                  }
                  if (meta.title) detailBits.push(`title: ${meta.title}`);
                  if (meta.source) detailBits.push(`source: ${meta.source}`);
                  return (
                    <div key={entry.id} className="text-xs border-l-2 pl-3 py-1" style={{ borderColor: color }}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium" style={{ color }}>{label}</span>
                        <span className="text-gray-400">{new Date(entry.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-gray-800">{name}</div>
                      {detailBits.length > 0 && (
                        <div className="text-gray-500">{detailBits.join(' · ')}</div>
                      )}
                      <div className="text-gray-400">by {entry.performed_by}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeForm} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h4 className="text-base font-bold text-gray-900 mb-4">
              {editingLeader ? 'Edit Leader' : 'Add Leader'}
            </h4>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="e.g. Jane Doe"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="e.g. CEO & Co-Founder"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                  placeholder="Brief bio..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">LinkedIn URL</label>
                  <input
                    type="url"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={orderPosition}
                  onChange={(e) => setOrderPosition(parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-24 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {formError && <p className="text-sm" style={{ color: '#EF4444' }}>{formError}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
